import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import PublicLayout from "./components/PublicLayout";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import LeadershipPage from "./pages/LeadershipPage";
import ContactPage from "./pages/ContactPage";
import InvoicesPage from "./pages/InvoicesPage";
import ReportsPage from "./pages/ReportsPage";
import TasksPage from "./pages/TasksPage";
import CashLedgerPage from "./pages/CashLedgerPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ClientFormPage from "./pages/ClientFormPage";
import ClientViewPage from "./pages/ClientViewPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import IncomeTaxPage from "./pages/IncomeTaxPage";
import GstPtPage from "./pages/GstPtPage";
import RegistrationPage from "./pages/RegistrationPage";
import PettyCashPage from "./pages/PettyCashPage";
import RentTrackerPage from "./pages/RentTrackerPage";
import DocumentsPage from "./pages/DocumentsPage";
import SettingsPage from "./pages/SettingsPage";
import QuotationsPage from "./pages/QuotationsPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import StockPage from "./pages/StockPage";
import GroupHomePage from "./pages/GroupHomePage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAF7]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-olive-600 font-body">Loading...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* SVT Aaventures Group Landing */}
          <Route path="/" element={<GroupHomePage />} />

          {/* SVT & Co Public Website */}
          <Route path="/svt-co" element={<PublicLayout />}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="leadership" element={<LeadershipPage />} />
            <Route path="contact" element={<ContactPage />} />
          </Route>

          {/* Legacy public paths -> redirect to /svt-co/* */}
          <Route path="/home" element={<Navigate to="/svt-co" replace />} />
          <Route path="/about" element={<Navigate to="/svt-co/about" replace />} />
          <Route path="/leadership" element={<Navigate to="/svt-co/leadership" replace />} />
          <Route path="/contact" element={<Navigate to="/svt-co/contact" replace />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />

          {/* Admin Portal */}
          <Route path="/admin" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/new" element={<ClientFormPage />} />
            <Route path="clients/:id/edit" element={<ClientFormPage />} />
            <Route path="clients/:id" element={<ClientViewPage />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="income-tax" element={<IncomeTaxPage />} />
            <Route path="gst-pt" element={<GstPtPage />} />
            <Route path="registrations" element={<RegistrationPage />} />
            <Route path="petty-cash" element={<PettyCashPage />} />
            <Route path="rent-tracker" element={<RentTrackerPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="cash-ledger" element={<AdminRoute><CashLedgerPage /></AdminRoute>} />
            <Route path="quotations" element={<QuotationsPage />} />
            <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="stock" element={<StockPage />} />
            <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
          </Route>

          {/* Default: redirect to group landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
